import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, MessageSquare, Phone, StickyNote, UserRound } from 'lucide-react';

import { cn } from '../../../lib/utils';

// The Workspace kit (Nic 2026-10-09): the look the Sales Workspace built on
// 2026-10-08, shared so the Client Workspace is the same room. The pieces
// that know about leads or calls stay in each workspace; these only draw.
// Spacing is in pixels on purpose: px-4 / px-5 / gap-4 / order-2 / border
// are also Bootstrap classes, bigger and !important.

export const CARD =
  'rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';
export const LABEL =
  'text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500';
// Matched, full-height panels; everything INSIDE a panel is a soft tile,
// never another card.
export const PANEL = cn(CARD, 'flex min-h-0 flex-col overflow-hidden');
export const TILE = 'rounded-xl bg-slate-50 dark:!bg-white/[0.04]';
export const RAIL_PAD = 'px-[16px] py-[12px]';

// The full-screen room: centered and capped on a big monitor, nearly all of
// a small one. While it's open the page behind it can't scroll (Nic
// 2026-10-09: the wheel moved the board underneath).
const useLockPageScroll = () => {
  useEffect(() => {
    const { body, documentElement: html } = document;
    const before = { body: body.style.overflow, html: html.style.overflow };
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = before.body;
      html.style.overflow = before.html;
    };
  }, []);
};
export const WorkspaceFrame = ({ children, z = 'z-[1078]' }) => {
  useLockPageScroll();
  return (
    <div className={cn('fixed inset-0 flex items-center justify-center overscroll-contain', z)}>
      <div className="absolute inset-0 bg-slate-900/50 dark:bg-black/70" />
      {/* Chrome can drop this rounded clip while something inside repaints
          (2026-10-22: hovering a History info button squared the corners),
          so the Workspaces round their own header and footer to match. */}
      <div className="relative flex h-full w-full flex-col overflow-hidden bg-[#F3F5F9] md:h-[calc(100%-2rem)] md:max-h-[960px] md:w-[calc(100%-2rem)] md:max-w-[1480px] md:rounded-3xl md:shadow-2xl dark:bg-surface-page">
        {children}
      </div>
    </div>
  );
};

// Where they are, as one slim line beside the name: done steps get a check,
// the current one is a dark pill, the rest are quiet. steps: [{ key, label }].
export const StepCrumb = ({ steps, index, label = 'Where they are' }) => (
  <ol aria-label={label} className="mb-0 flex list-none flex-wrap items-center gap-[4px] pl-0">
    {steps.map((j, i) => (
      <li key={j.key} className="flex items-center gap-[4px]">
        <span
          aria-current={i === index ? 'step' : undefined}
          className={cn(
            'inline-flex items-center gap-[4px] whitespace-nowrap rounded-full py-[2px] text-[11px] font-semibold',
            // Only the current step is a pill, so the line's first word
            // starts flush with the name above it; a first-step pill hangs
            // its padding out to the left for the same reason.
            i < index
              ? 'text-emerald-700 dark:text-emerald-300'
              : i === index
                ? cn(
                    'bg-slate-900 px-[8px] text-white dark:!bg-slate-100 dark:!text-slate-900',
                    i === 0 && '-ml-[8px]',
                  )
                : 'text-slate-400 dark:text-slate-500',
          )}
        >
          {i < index ? <Check size={11} strokeWidth={3} /> : null}
          {j.label}
        </span>
        {i < steps.length - 1 ? (
          <ChevronRight size={12} className="text-slate-300 dark:text-slate-600" aria-hidden />
        ) : null}
      </li>
    ))}
  </ol>
);

// A quiet fact beside the name: "Referral · Tasha Wells", "VA.gov: Yes".
export const HeaderChip = ({ label, children, muted = false, title }) => (
  <span
    title={title}
    className="inline-flex max-w-[16rem] items-center gap-[4px] truncate rounded-md bg-slate-100 px-[6px] py-[2px] text-[11px] text-slate-600 dark:!bg-white/[0.06] dark:text-slate-300"
  >
    {label ? <span className="text-slate-400 dark:text-slate-500">{label}</span> : null}
    <span className={cn('truncate', muted ? 'italic text-slate-400 dark:text-slate-500' : 'font-semibold')}>
      {children}
    </span>
  </span>
);

// Section headers with some life: a tinted icon tile per section and a
// darker label. One tone per section, both themes.
export const SECTION_TONE = {
  sky: 'bg-sky-100 text-sky-600 dark:!bg-sky-500/15 dark:text-sky-300',
  // What they told us (Nic 2026-10-21: blue, apart from About them's teal).
  blue: 'bg-blue-100 text-blue-600 dark:!bg-blue-500/15 dark:text-blue-300',
  amber: 'bg-amber-100 text-amber-600 dark:!bg-amber-500/15 dark:text-amber-300',
  violet: 'bg-violet-100 text-violet-600 dark:!bg-violet-500/15 dark:text-violet-300',
  teal: 'bg-teal-100 text-teal-600 dark:!bg-teal-500/15 dark:text-teal-300',
  emerald: 'bg-emerald-100 text-emerald-600 dark:!bg-emerald-500/15 dark:text-emerald-300',
  indigo: 'bg-indigo-100 text-indigo-600 dark:!bg-indigo-500/15 dark:text-indigo-300',
  rose: 'bg-rose-100 text-rose-600 dark:!bg-rose-500/15 dark:text-rose-300',
  slate: 'bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-300',
};

// The work area's tabs, one color each: a closed tab is a soft fill of its
// color; the open one is white, outlined in its color, and the page under it
// takes the same outline, so tab and page read as one.
export const TAB_TONE = {
  sky: {
    idle: 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 dark:border-sky-500/25 dark:!bg-sky-500/10 dark:text-sky-200 dark:hover:!bg-sky-500/20',
    line: 'border-sky-400 dark:border-sky-400/60',
    badge: 'bg-sky-100 text-sky-700 dark:!bg-sky-500/15 dark:text-sky-300',
  },
  indigo: {
    idle: 'border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 dark:border-indigo-500/25 dark:!bg-indigo-500/10 dark:text-indigo-200 dark:hover:!bg-indigo-500/20',
    line: 'border-indigo-400 dark:border-indigo-400/60',
    badge: 'bg-indigo-100 text-indigo-700 dark:!bg-indigo-500/15 dark:text-indigo-300',
  },
  violet: {
    idle: 'border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100 dark:border-violet-500/25 dark:!bg-violet-500/10 dark:text-violet-200 dark:hover:!bg-violet-500/20',
    line: 'border-violet-400 dark:border-violet-400/60',
    badge: 'bg-violet-100 text-violet-700 dark:!bg-violet-500/15 dark:text-violet-300',
  },
  emerald: {
    idle: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-500/25 dark:!bg-emerald-500/10 dark:text-emerald-200 dark:hover:!bg-emerald-500/20',
    line: 'border-emerald-400 dark:border-emerald-400/60',
    badge: 'bg-emerald-100 text-emerald-700 dark:!bg-emerald-500/15 dark:text-emerald-300',
  },
  teal: {
    idle: 'border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 dark:border-teal-500/25 dark:!bg-teal-500/10 dark:text-teal-200 dark:hover:!bg-teal-500/20',
    line: 'border-teal-400 dark:border-teal-400/60',
    badge: 'bg-teal-100 text-teal-700 dark:!bg-teal-500/15 dark:text-teal-300',
  },
  // Transcripts (Nic 2026-10-21: its own color on both Workspaces, so
  // green stays Sign up's).
  rose: {
    idle: 'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 dark:border-rose-500/25 dark:!bg-rose-500/10 dark:text-rose-200 dark:hover:!bg-rose-500/20',
    line: 'border-rose-400 dark:border-rose-400/60',
    badge: 'bg-rose-100 text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300',
  },
  amber: {
    idle: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-500/25 dark:!bg-amber-500/10 dark:text-amber-200 dark:hover:!bg-amber-500/20',
    line: 'border-amber-400 dark:border-amber-400/60',
    badge: 'bg-amber-100 text-amber-700 dark:!bg-amber-500/15 dark:text-amber-300',
  },
};

export const SectionHead = ({ icon: Icon, tone = 'slate', children, right = null }) => (
  <div className="flex items-center gap-[8px]">
    <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md', SECTION_TONE[tone])}>
      <Icon size={13} strokeWidth={2.25} />
    </span>
    <span className="text-[12px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
      {children}
    </span>
    {right ? <span className="ml-auto">{right}</span> : null}
  </div>
);

// A titled group of label/value rows.
export const PersonGroup = ({ title, icon, tone, children }) => (
  <div className={RAIL_PAD}>
    {title ? (
      <div className="mb-[8px]">
        <SectionHead icon={icon} tone={tone}>
          {title}
        </SectionHead>
      </div>
    ) : null}
    <dl className="mb-0">{children}</dl>
  </div>
);

// lit: a row lights in a color (an armed send card lights the row it will
// send to). Color and ring only: nothing moves or grows.
const ROW_LIT = {
  sky: 'bg-sky-50 ring-1 ring-sky-300 dark:!bg-sky-500/10 dark:ring-sky-400/50',
  violet: 'bg-violet-50 ring-1 ring-violet-300 dark:!bg-violet-500/10 dark:ring-violet-400/50',
};
const ROW_LIT_TEXT = {
  sky: 'text-sky-800 dark:text-sky-200',
  violet: 'text-violet-800 dark:text-violet-200',
};
export const PersonRow = ({ label, title, muted = false, lit = null, children }) => (
  <div
    className={cn(
      '-mx-[8px] flex items-baseline justify-between gap-[12px] rounded-md px-[8px] py-[2px]',
      lit && ROW_LIT[lit],
    )}
  >
    <dt className="shrink-0 text-xs font-normal text-slate-400 dark:text-slate-500">{label}</dt>
    <dd
      title={title}
      className={cn(
        'mb-0 min-w-0 truncate text-right text-[13px] font-medium tabular-nums',
        lit
          ? ROW_LIT_TEXT[lit]
          : muted
            ? 'italic text-slate-400 dark:text-slate-500'
            : 'font-medium text-slate-700 dark:text-slate-200',
      )}
    >
      {children}
    </dd>
  </div>
);

// Notes in the rail: grows with what's written, from a few lines up to a
// cap, then scrolls. Ctrl + Enter saves. onSave(text) resolves when saved.
const NOTES_MIN_PX = 96;
const NOTES_MAX_PX = 320;
export const NotesEditor = ({
  initial = '',
  onSave,
  saving = false,
  readOnly = false,
  placeholder,
  title = 'Notes',
  sub = null,
}) => {
  const [text, setText] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const boxRef = useRef(null);
  const dirty = text !== saved;
  const doSave = async () => {
    try {
      await onSave(text);
      setSaved(text);
    } catch {
      // The caller says what went wrong.
    }
  };
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(NOTES_MAX_PX, Math.max(NOTES_MIN_PX, el.scrollHeight))}px`;
  }, [text]);
  return (
    <div className={RAIL_PAD}>
      <div className="mb-[8px]">
        <SectionHead
          icon={StickyNote}
          tone="amber"
          right={
            <span
              className={cn(
                'text-[11px]',
                dirty ? 'font-semibold text-amber-600 dark:text-amber-300' : 'text-slate-400',
              )}
            >
              {saving ? 'Saving...' : dirty ? 'Not saved' : saved ? 'Saved' : ''}
            </span>
          }
        >
          {title}
        </SectionHead>
        {sub ? <div className="mt-[2px] pl-[32px] text-[11.5px] text-slate-400">{sub}</div> : null}
      </div>
      <textarea
        ref={boxRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        readOnly={readOnly}
        onKeyDown={(e) => {
          if (readOnly) return;
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') doSave();
        }}
        placeholder={placeholder}
        className="block w-full resize-none rounded-lg border border-solid border-slate-300 bg-white px-[10px] py-[8px] text-[13px] leading-relaxed text-slate-700 outline-none focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/25 dark:border-white/20 dark:!bg-white/5 dark:text-slate-200 dark:focus:border-sky-400/70"
      />
      {!readOnly ? (
        <div className="mt-[6px] flex items-center justify-between">
          <span className="text-[11px] text-slate-400">Ctrl + Enter saves</span>
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={doSave}
            className="whitespace-nowrap rounded-md bg-slate-900 px-[10px] py-[4px] text-[11px] font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:bg-slate-200 dark:!text-slate-900"
          >
            Save
          </button>
        </div>
      ) : null}
    </div>
  );
};

// Folder tabs: the tabs sit on the Workspace background above the page;
// the page IS the open tab's. tabs: [{ key, label, icon, tone, badge,
// disabled, end }] (end: pushed to the right).
// Too narrow for every name (Nic 2026-10-21: "O...", "If ...", "Tr..."):
// the closed tabs drop to their icon, the open one keeps its name. A hidden
// copy of the row with every name measures whether they'd fit.
const useTabsFit = (deps) => {
  const boxRef = useRef(null);
  const fullRef = useRef(null);
  const [fits, setFits] = useState(true);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const full = fullRef.current;
    if (!box || !full) return undefined;
    const check = () => setFits(full.scrollWidth <= box.clientWidth + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(box);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { boxRef, fullRef, fits };
};

const TabFace = ({ t, showLabel, showBadge }) => (
  <>
    <span
      className={cn(
        // Phones keep the icon, smaller; the badges hide so tabs fit.
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-md max-sm:h-5 max-sm:w-5',
        SECTION_TONE[t.tone],
      )}
    >
      <t.icon size={13} strokeWidth={2.25} />
    </span>
    {showLabel ? <span className="min-w-0 truncate">{t.label}</span> : <span className="sr-only">{t.label}</span>}
    {showBadge && t.badge ? (
      <span className={cn('shrink-0 rounded-full px-[7px] py-px text-[11px] font-semibold tabular-nums max-sm:hidden', TAB_TONE[t.tone].badge)}>
        {t.badge}
      </span>
    ) : null}
  </>
);

const TAB_BASE =
  'relative inline-flex items-center gap-[6px] whitespace-nowrap rounded-t-xl border-[1px] border-b-0 px-[9px] text-[13px] font-semibold sm:gap-[8px] sm:px-[14px] sm:text-sm outline-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-40';

export const FolderTabs = ({ tabs, active, onSelect, label = 'What to work on' }) => {
  const { boxRef, fullRef, fits } = useTabsFit([tabs.map((t) => `${t.key}:${t.label}:${t.badge || ''}`).join('|'), active]);
  return (
    <div
      ref={boxRef}
      role="tablist"
      aria-label={label}
      className="relative z-10 flex min-w-0 items-end gap-[4px] px-[12px] sm:gap-[6px] sm:px-[16px]"
    >
      {/* The measuring copy: every name, never seen, clipped to nothing so
          it can never widen the page (a phone scrolled sideways to it). */}
      <div aria-hidden="true" className="pointer-events-none invisible absolute inset-0 overflow-hidden">
      <div
        ref={fullRef}
        className="flex w-max items-end gap-[4px] px-[12px] sm:gap-[6px] sm:px-[16px]"
      >
        {tabs.map((t) => (
          <span key={t.key} className={cn(TAB_BASE, 'h-10 border-transparent', t.key === active && 'border-[2px]')}>
            <TabFace t={t} showLabel showBadge />
          </span>
        ))}
      </div>
      </div>
      {tabs.map((t) => {
        const on = t.key === active;
        const tone = TAB_TONE[t.tone];
        const iconOnly = !fits && !on;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            aria-label={iconOnly ? t.label : undefined}
            disabled={t.disabled}
            onClick={() => onSelect(t.key)}
            title={t.label}
            className={cn(
              // Narrow: closed tabs go to their icon; the open one keeps its
              // name. Never past the page's edge (Nic 2026-10-12).
              TAB_BASE,
              on ? 'shrink-0' : iconOnly ? 'shrink-0 !px-[7px]' : 'min-w-[2.75rem] shrink',
              // No focus ring: the open tab's own outline says which is picked.
              t.end && 'ml-auto',
              on
                ? cn(
                    // Taller, and over the page's top border so the outline
                    // runs around both as one shape.
                    '-mb-[2px] h-11 border-[2px] border-b-0 bg-white text-slate-900 dark:!bg-surface-card dark:text-white',
                    tone.line,
                  )
                : cn('h-10', tone.idle),
            )}
          >
            <TabFace t={t} showLabel={!iconOnly} showBadge={!iconOnly} />
          </button>
        );
      })}
    </div>
  );
};

// The open tab's page: a 2px outline in its color, scrolling in an inset
// box so the scrollbar sits a few pixels in from the border. inner: the
// centered reading column (false: the child lays itself out).
export const TabPage = ({ tone = 'sky', inner = true, children }) => (
  <div
    className={cn(
      'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-[2px] bg-white py-[6px] pr-[5px] shadow-[0_2px_10px_rgba(15,23,42,0.05)] dark:!bg-surface-card',
      TAB_TONE[tone].line,
    )}
  >
    <div className={cn('os-scroll min-h-0 flex-1 overflow-y-auto', `os-thumb-${tone}`)}>
      {inner ? <div className="mx-auto max-w-3xl px-[16px] py-[10px] xl:px-[20px]">{children}</div> : children}
    </div>
  </div>
);

// ---- One look for both Workspaces (Nic 2026-10-21) --------------------------
// The Onboarding Workspace's About them card, Text and Call, used by the
// Sales Workspace too.

const phoneText = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : p || '';
};

// The left card: a teal title, then its groups (About them rows, What they
// told us, History), scrolling on its own.
export const AboutCard = ({ children }) => (
  <section
    aria-label="About them"
    className="flex min-h-0 flex-col overflow-hidden rounded-2xl bg-white py-[6px] pr-[5px] shadow-[0_2px_10px_rgba(15,23,42,0.05)] dark:!bg-surface-card"
  >
    <div className="flex items-center gap-[8px] px-[16px] pb-[4px] pt-[8px]">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-100 text-teal-600 dark:!bg-teal-500/15 dark:text-teal-300">
        <UserRound size={15} strokeWidth={2.25} />
      </span>
      <h3 className="mb-0 text-[15px] font-bold text-slate-900 dark:text-white">About them</h3>
    </div>
    <div className="os-scroll os-thumb-teal min-h-0 flex-1 overflow-y-auto">
      <div className="divide-y divide-slate-100 dark:divide-white/10">{children}</div>
    </div>
  </section>
);

export const TextButton = ({ onClick, label = 'Text', disabled = false, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full bg-sky-600 px-3.5 text-sm font-semibold text-white outline-none hover:bg-sky-500 focus:outline-none disabled:opacity-50"
  >
    <MessageSquare size={14} /> {label}
  </button>
);

// A beat before dialing (Nic 2026-10-10): who, which number, then Call now.
// Anchored under the Call button; Enter calls, Escape or a click away
// cancels.
export const CallConfirm = ({ name, phone, onConfirm, onCancel }) => {
  const ref = useRef(null);
  const callBtn = useRef(null);
  // The latest onCancel, so a parent re-render never re-runs the effect
  // (which moved focus back to Call now each time, branch QA 2026-10-21).
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    callBtn.current?.focus();
    const onDown = (e) => {
      // The Call button that opened it toggles it itself; a click there is
      // not "outside" (it used to close, then reopen at once).
      const box = ref.current?.parentElement || ref.current;
      if (box && !box.contains(e.target)) cancelRef.current();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cancelRef.current();
      }
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, []);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Call ${name}`}
      className="absolute right-0 top-full z-30 mt-2 w-[18rem] rounded-2xl bg-white p-4 text-left shadow-[0_4px_12px_rgba(15,23,42,0.08),0_16px_40px_rgba(15,23,42,0.16)] ring-1 ring-slate-200/70 dark:!bg-surface-elevated dark:ring-white/10"
    >
      <div className="flex items-center gap-3">
        <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-[0_4px_12px_rgba(16,185,129,0.35)]">
          <Phone size={18} strokeWidth={2.25} />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[15px] font-bold leading-tight text-slate-900 dark:text-white">Call {name}?</div>
          <div className="mt-0.5 text-[13px] font-medium tabular-nums text-slate-500 dark:text-slate-400">{phoneText(phone)}</div>
        </div>
      </div>
      <p className="mb-0 mt-3 text-[11.5px] leading-relaxed text-slate-400 dark:text-slate-500">
        Stays connected wherever you go in the app.
      </p>
      <div className="mt-3.5 flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-9 flex-1 rounded-xl bg-slate-100 text-sm font-semibold text-slate-600 outline-none hover:bg-slate-200 focus:outline-none dark:!bg-white/10 dark:text-slate-300 dark:hover:!bg-white/15"
        >
          Cancel
        </button>
        <button
          ref={callBtn}
          type="button"
          onClick={onConfirm}
          className="inline-flex h-9 flex-[1.4] items-center justify-center gap-1.5 rounded-xl bg-emerald-600 text-sm font-semibold text-white shadow-[0_2px_8px_rgba(16,185,129,0.35)] outline-none hover:bg-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
        >
          <Phone size={14} /> Call now
        </button>
      </div>
    </div>
  );
};

// Call, then the confirm under it. open / onOpenChange: the Workspace keeps
// it, so its own Escape can leave the confirm to close first.
export const CallButton = ({ name, phone, open, onOpenChange, onCall, label = 'Call', disabled = false, title }) => (
  <span className="relative inline-flex">
    <button
      type="button"
      disabled={disabled}
      title={title}
      aria-expanded={open}
      onClick={() => onOpenChange(!open)}
      className="inline-flex h-8 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-600 px-3.5 text-sm font-semibold text-white outline-none hover:bg-emerald-500 focus:outline-none disabled:opacity-50"
    >
      <Phone size={14} /> {label}
    </button>
    {open ? (
      <CallConfirm
        name={name || 'them'}
        phone={phone}
        onCancel={() => onOpenChange(false)}
        onConfirm={() => {
          onOpenChange(false);
          onCall();
        }}
      />
    ) : null}
  </span>
);

// ---- Manage: one row per thing to do (both Workspaces) ----------------------

export const ManageLabel = ({ children }) => (
  <div className="mb-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
    {children}
  </div>
);

// One Manage row (Nic 2026-10-15; both Workspaces since 2026-10-21): icon, title and one line. Showing every
// form at once read as "do all three", so a row opens its form only when
// clicked, and opening one closes the others.
const MANAGE_CARD_TONE = {
  emerald: 'bg-emerald-100 text-emerald-700 dark:!bg-emerald-500/15 dark:text-emerald-300',
  sky: 'bg-sky-100 text-sky-700 dark:!bg-sky-500/15 dark:text-sky-300',
  violet: 'bg-violet-100 text-violet-700 dark:!bg-violet-500/15 dark:text-violet-300',
  rose: 'bg-rose-100 text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:!bg-indigo-500/15 dark:text-indigo-300',
};
// The OPEN row wears its own color (Nic 2026-10-15: make the picked one
// unmistakable): a colored edge, a tinted header and a solid icon tile.
const MANAGE_OPEN_TONE = {
  emerald: {
    card: 'ring-[1.5px] ring-emerald-400 dark:ring-emerald-400/70',
    head: 'bg-emerald-50 dark:!bg-emerald-500/10',
    icon: 'bg-emerald-600 text-white dark:!bg-emerald-500 dark:!text-white',
    line: 'border-emerald-100 dark:border-emerald-400/20',
  },
  sky: {
    card: 'ring-[1.5px] ring-sky-400 dark:ring-sky-400/70',
    head: 'bg-sky-50 dark:!bg-sky-500/10',
    icon: 'bg-sky-600 text-white dark:!bg-sky-500 dark:!text-white',
    line: 'border-sky-100 dark:border-sky-400/20',
  },
  violet: {
    card: 'ring-[1.5px] ring-violet-400 dark:ring-violet-400/70',
    head: 'bg-violet-50 dark:!bg-violet-500/10',
    icon: 'bg-violet-600 text-white dark:!bg-violet-500 dark:!text-white',
    line: 'border-violet-100 dark:border-violet-400/20',
  },
  rose: {
    card: 'ring-[1.5px] ring-rose-400 dark:ring-rose-400/70',
    head: 'bg-rose-50 dark:!bg-rose-500/10',
    icon: 'bg-rose-600 text-white dark:!bg-rose-500 dark:!text-white',
    line: 'border-rose-100 dark:border-rose-400/20',
  },
  indigo: {
    card: 'ring-[1.5px] ring-indigo-400 dark:ring-indigo-400/70',
    head: 'bg-indigo-50 dark:!bg-indigo-500/10',
    icon: 'bg-indigo-600 text-white dark:!bg-indigo-500 dark:!text-white',
    line: 'border-indigo-100 dark:border-indigo-400/20',
  },
};
export const ManageCard = ({
  icon: Icon,
  tone,
  title,
  blurb,
  open,
  dimmed,
  onToggle,
  children,
  footer,
}) => {
  const on = MANAGE_OPEN_TONE[tone];
  return (
    // Closed: a soft grey row, faded back while another row is open. Open: a
    // white card edged in its color (no focus ring on the header, which only
    // wrapped half).
    <section
      className={cn(
        'rounded-2xl transition-[background-color,box-shadow,opacity]',
        open
          ? cn('bg-white shadow-[0_4px_16px_rgba(15,23,42,0.08)] dark:!bg-white/[0.06]', on.card)
          : 'bg-slate-50 dark:!bg-white/[0.04]',
        dimmed && 'opacity-60 hover:opacity-100',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3 text-left outline-none focus:outline-none',
          open ? cn('rounded-t-2xl', on.head) : 'rounded-2xl hover:bg-slate-100 dark:hover:!bg-white/[0.06]',
        )}
      >
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
            open ? on.icon : MANAGE_CARD_TONE[tone],
          )}
        >
          <Icon size={17} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold text-slate-900 dark:text-slate-100">
            {title}
          </span>
          <span className="block text-xs leading-snug text-slate-500 dark:text-slate-400">
            {blurb}
          </span>
        </span>
        <ChevronDown
          size={16}
          className={cn(
            'shrink-0 text-slate-400 dark:text-slate-500',
            open && 'rotate-180',
          )}
        />
      </button>
      {open ? (
        // Top to bottom (Nic 2026-10-15): labeled field, then the action,
        // then any note, all lined up under the title.
        <div className={cn('space-y-3 border-t px-4 pb-4 pt-3.5 sm:pl-16', on.line)}>
          {children}
          {footer}
        </div>
      ) : null}
    </section>
  );
};
